import React from 'react';
import { Button, Checkbox, Col, Flex, Form, Row, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { CustomInput, CustomSelect } from '../../../components';
import {
  CRYO_CURRENCY_OPTIONS,
  CRYO_FIXED_ACCESSORIES,
  CRYO_HIGH_VOLUME_RACK_ID,
  CRYO_RACK_OPTIONS,
  CRYO_SAFEPAK_900,
} from '../utils/cryoportOptions';
import styles from './CryoShipperFormBlock.module.css';

const readOnlyInputStyle: React.CSSProperties = {
  backgroundColor: 'var(--readonly-field-bg)',
  cursor: 'not-allowed',
};

const Required: React.FC = () => <span className={styles.required}>*</span>;

/** Cryoportal Step 2 block: commodities, fixed accessories and the rack picker. */
const CryoShipperFormBlock: React.FC = () => {
  const form = Form.useFormInstance();
  const cryoRackId = Form.useWatch('cryo_rack_id', form);
  return (
    <>
      <div className={styles.commodities}>
        <label className={styles.label}>
          Commodities <Required />
        </label>
        <Form.List name='cryo_commodities'>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div key={field.key} className={styles.commodityPanel}>
                  <Flex
                    justify='space-between'
                    align='center'
                    className={styles.commodityHeader}
                  >
                    <Typography.Text strong className={styles.commodityTitle}>
                      Commodity {index + 1}
                    </Typography.Text>
                    <Button
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      size='small'
                    />
                  </Flex>

                  <Row gutter={[12, 0]}>
                    <Col span={12}>
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>Commodity</label>
                        <Form.Item
                          name={[field.name, 'commodity_id']}
                          noStyle
                          initialValue='12878'
                        >
                          <CustomInput style={{ display: 'none' }} />
                        </Form.Item>
                        <CustomInput
                          value='PHARMA'
                          height='36px'
                          disabled
                          style={readOnlyInputStyle}
                        />
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>Lot/Serial #</label>
                        <Form.Item name={[field.name, 'lot_number']} noStyle>
                          <CustomInput placeholder='Optional' height='36px' />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={[12, 0]}>
                    <Col span={8}>
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>
                          Quantity <Required />
                        </label>
                        <Form.Item
                          name={[field.name, 'quantity']}
                          rules={[{ required: true, message: 'Required' }]}
                          noStyle
                        >
                          <CustomInput placeholder='1' height='36px' />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={16}>
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>
                          Currency <Required />
                        </label>
                        <Form.Item
                          name={[field.name, 'price_currency']}
                          noStyle
                        >
                          <CustomSelect
                            placeholder='USD'
                            width='100%'
                            height='36px'
                            showSearch
                            optionFilterProp='label'
                            options={CRYO_CURRENCY_OPTIONS}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={[12, 0]}>
                    <Col span={12}>
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>
                          <Required /> Declared value (per unit)
                        </label>
                        <Form.Item
                          name={[field.name, 'price_per_unit']}
                          rules={[{ required: true, message: 'Required' }]}
                          initialValue='1.00'
                          noStyle
                        >
                          <CustomInput placeholder='1.00' height='36px' />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>
                          <Required /> Declared value (total)
                        </label>
                        {/* shouldUpdate re-renders whenever any form value changes,
                            computing total = qty × price without onChange conflicts */}
                        <Form.Item shouldUpdate noStyle>
                          {() => {
                            const qty =
                              parseFloat(
                                String(
                                  form.getFieldValue([
                                    'cryo_commodities',
                                    field.name,
                                    'quantity',
                                  ])
                                )
                              ) || 0;
                            const price =
                              parseFloat(
                                String(
                                  form.getFieldValue([
                                    'cryo_commodities',
                                    field.name,
                                    'price_per_unit',
                                  ])
                                )
                              ) || 0;
                            return (
                              <CustomInput
                                value={(qty * price).toFixed(2)}
                                placeholder='1.00'
                                height='36px'
                                readOnly
                                style={readOnlyInputStyle}
                              />
                            );
                          }}
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Form.Item
                    name={[field.name, 'leg_2']}
                    valuePropName='checked'
                    initialValue={true}
                    noStyle
                  >
                    <Checkbox>Assign to Leg 2</Checkbox>
                  </Form.Item>
                </div>
              ))}

              <Button
                type='dashed'
                onClick={() =>
                  add({
                    commodity_id: '12878',
                    quantity: 1,
                    price_currency: 'USD',
                    price_per_unit: '1.00',
                    price_total: '1.00',
                    country_of_origin: 'US',
                    leg_2: true,
                  })
                }
                icon={<PlusOutlined />}
                className={styles.addCommodity}
              >
                Add Commodity
              </Button>
            </>
          )}
        </Form.List>
      </div>

      <div className={styles.accessories}>
        <label className={styles.label}>Accessories</label>

        {/* Fixed accessories — always added, not editable.
            Safepak 900 is included here when High Volume rack is selected. */}
        <div className={styles.fixedAccessories}>
          {[
            ...CRYO_FIXED_ACCESSORIES,
            ...(cryoRackId === CRYO_HIGH_VOLUME_RACK_ID
              ? [CRYO_SAFEPAK_900]
              : []),
          ].map((acc) => (
            <div key={acc.id} className={styles.accessoryRow}>
              <span className={styles.accessoryQty}>1×</span>
              <span>{acc.label}</span>
            </div>
          ))}
        </div>

        {/* Rack selection — user picks exactly one */}
        <div className={styles.fieldWrap}>
          <label className={styles.label}>
            <Required />
            {'  '}Rack
          </label>
          <Form.Item
            name='cryo_rack_id'
            rules={[{ required: true, message: 'Please select a rack' }]}
            noStyle
          >
            <CustomSelect
              placeholder='Select a rack...'
              width='100%'
              height='36px'
              options={CRYO_RACK_OPTIONS}
            />
          </Form.Item>
        </div>
      </div>
    </>
  );
};

export default CryoShipperFormBlock;
